"use client";

import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import Link from "next/link";

import Footer from "../../components/layout/footer";
import Header from "../../components/layout/header";

const API_BASE = process.env.NEXT_PUBLIC_API_BASE || "http://localhost:8000";

const COLORS = {
  primary: "#003B5C",
  teal: "#00778B",
  gray: "#C7C9C7",
  gold: "#E3A130",
};

const tw = {
  primaryBg: "bg-[#003B5C]",
  primaryText: "text-[#003B5C]",
  tealBg: "bg-[#00778B]",
  tealText: "text-[#00778B]",
  goldBg: "bg-[#E3A130]",
  goldText: "text-[#E3A130]",
  grayText: "text-[#C7C9C7]",
};

// URL PDF MICE (Drive)
const MICE_PDF_URL =
  "https://drive.google.com/file/d/1zKQ4cmUNt-JElaSmGv_YGMdObV7xZW1W/view";

const getDrivePreviewUrl = (url) => {
  if (!url) return "";
  if (url.includes("/view")) return url.replace("/view", "/preview");
  return url;
};



const formatPriceDot = (value) => {
  const num = Number(value);
  if (!isFinite(num)) return "0.00";
  const sign = num < 0 ? "-" : "";
  const n = Math.abs(num);
  const [int, dec] = n.toFixed(2).split(".");
  const intWithDots = int.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `${sign}${intWithDots}.${dec}`;
};

// ==========================
// CARD VENUE (MICE STYLE)
// ==========================
const ProductCard = ({ product, href, onAddToCart }) => {
  const img =
    product.images?.find((im) => im.type === "front")?.url ||
    product.images?.[0]?.url ||
    "/product-placeholder.jpg";

  return (
    <motion.div
      whileHover={{ y: -4 }}
      className="group rounded-2xl overflow-hidden bg-[#F5F7F9] border border-zinc-200 shadow-sm hover:shadow-md transition flex flex-col"
    >
      {/* Klik gambar / judul ke detail */}
      <a href={href} className="block relative">
        <img
          src={img}
          alt={product.name}
          className="w-full h-56 sm:h-64 object-cover"
        />
        {Number(product.stock) <= 0 && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <span className="text-white font-bold bg-red-500 px-2 py-1 rounded">
              Out of Stock
            </span>
          </div>
        )}
      </a>

      <div className="px-5 py-4 flex flex-col flex-1">
        <div className="flex-1 space-y-1 text-sm">
          <h3 className="font-semibold text-slate-900 mb-1 line-clamp-2">
            <a href={href} className="hover:underline">
              {product.name}
            </a>
          </h3>

          {/* Opsional: jika ada lokasi */}
          {product.location && (
            <p className="text-xs text-slate-600">{product.location}</p>
          )}

          <div
            className="text-xs text-slate-700 leading-relaxed max-h-[5.5rem] overflow-y-auto pr-2"
            dangerouslySetInnerHTML={{
              __html:
                product.description ||
                "<p class='text-slate-500'>No description available</p>",
            }}
          />

        </div>

        <div className="mt-5">
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onAddToCart && onAddToCart(product);
            }}
            disabled={Number(product.stock) <= 0}
            className="inline-flex items-center justify-center px-5 py-2 rounded-full text-xs font-semibold bg-[#003B5C] text-white hover:bg-[#00263C] disabled:opacity-60 disabled:cursor-not-allowed transition"
          >
            BOOK NOW
          </button>
        </div>
      </div>
    </motion.div>
  );
};

// ==========================
// MAIN PAGE
// ==========================
export default function HomePage() {
  const [darkMode, setDarkMode] = useState(false);
  const [mounted, setMounted] = useState(false);

  // --- Dark mode init ---
  useEffect(() => {
    const saved =
      typeof window !== "undefined" ? localStorage.getItem("darkMode") : null;
    const prefers =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-color-scheme: dark)").matches;
    const initial = saved !== null ? saved === "true" : prefers;
    setDarkMode(initial);
    if (typeof document !== "undefined") {
      document.documentElement.classList.toggle("dark", initial);
    }
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted) return;
    if (darkMode) document.documentElement.classList.add("dark");
    else document.documentElement.classList.remove("dark");
    if (typeof window !== "undefined") {
      localStorage.setItem("darkMode", String(darkMode));
    }
  }, [darkMode, mounted]);

  const toggleDarkMode = () => setDarkMode((p) => !p);

  // ---------- Produk "mice" ----------
  const FIXED_CATEGORY = "mice";
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const PRODUCT_PER_SLIDE = 6;
  const [prodPage, setProdPage] = useState(1);

  // ---------- CART ----------
  const [cartItems, setCartItems] = useState([]);
  const [showCart, setShowCart] = useState(false);

  const cartCount = cartItems.reduce(
    (sum, item) => sum + Number(item.quantity || 0),
    0
  );

  // PDF embed toggle
  const [showPdf, setShowPdf] = useState(false);

  // Load cart dari localStorage saat pertama kali mount
  useEffect(() => {
    if (!mounted) return;
    if (typeof window === "undefined") return;

    try {
      const raw = localStorage.getItem("cart") || "[]";
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        setCartItems(parsed);
      }
    } catch (e) {
      console.error("Failed to parse cart from localStorage", e);
    }
  }, [mounted]);

  // ==============
  // ADD TO CART
  // ==============
  const handleAddToCart = (product) => {
    try {
      if (typeof window === "undefined") return;

      const stock = Number(product.stock) || 0;
      if (stock <= 0) {
        alert("Maaf, stok habis.");
        return;
      }

      const getFrontImage = (p) => {
        const fromType =
          p.images?.find((im) => im.type === "front")?.url || null;
        return fromType || p.images?.[0]?.url || "/product-placeholder.jpg";
      };

      const raw = localStorage.getItem("cart") || "[]";
      let cart = [];
      try {
        const parsed = JSON.parse(raw);
        cart = Array.isArray(parsed) ? parsed : [];
      } catch {
        cart = [];
      }

      const existing = cart.find((item) => item.id === product.id);
      if (existing) {
        const currentQty = Number(existing.quantity || 0);
        if (currentQty >= stock) {
          alert(`Maksimal pembelian untuk produk ini adalah ${stock} pcs.`);
          return;
        }
        existing.quantity = currentQty + 1;
      } else {
        cart.push({
          id: product.id,
          name: product.name,
          price: product.price,
          image: getFrontImage(product),
          quantity: 1,
          store: {
            id: product.vendor?.id ?? "dpu-shop",
            name: product.vendor?.storeName ?? "MICE",
            phone: product.vendor?.phone ?? "",
          },
        });
      }

      localStorage.setItem("cart", JSON.stringify(cart));
      setCartItems(cart);
      window.dispatchEvent(new Event("cartUpdated"));
      setShowCart(true);
    } catch (e) {
      console.error("Cart error:", e);
    }
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);
      setErr("");

      const collected = [];
      let page = 1;
      let totalPages = 1;
      const limit = 100;

      do {
        const params = new URLSearchParams({
          page: String(page),
          limit: String(limit),
        });
        params.append("category", FIXED_CATEGORY);

        const res = await fetch(`${API_BASE}/products?${params.toString()}`);
        const json = await res.json();
        if (!res.ok) throw new Error(json.detail || "Failed to load products");

        collected.push(...json.data);
        totalPages = json.meta.pages;
        page++;
      } while (page <= totalPages);

      setAllProducts(collected);
      setProdPage(1);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const prodTotalPages =
    Math.ceil(allProducts.length / PRODUCT_PER_SLIDE) || 1;
  const prodStart = (prodPage - 1) * PRODUCT_PER_SLIDE;
  const prodSlice = allProducts.slice(
    prodStart,
    prodStart + PRODUCT_PER_SLIDE
  );

  if (!mounted) return null;

  return (
    <div className="">
      <Header
        darkMode={darkMode}
        toggleDarkMode={toggleDarkMode}
        cartCount={cartCount}
      />

      {/* ================= PRODUCTS (ONLY MICE) ================= */}
      <section id="products" className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-4 text-[#003B5C]">
            About Meeting, Incentive, Convention, and Exhibition
          </h2>
          <p className="text-sm sm:text-base text-zinc-700 max-w-7xl mb-4">
            Our MICE (Meeting, Incentive, Convention, and Exhibition) services
            are designed to elevate your professional events, whether you’re
            hosting an intimate meeting, a strategic corporate gathering, or a
            large-scale conference. We provide flexible, high-quality venues
            supported by modern facilities and a comfortable, exclusive
            environment. In addition, we offer premium location rentals for
            video and film production, as well as other creative content needs.
            Our spaces provide versatile backdrops, exceptional atmosphere, and
            a visually compelling setting to bring your creative ideas to life.
          </p>

          {/* BUTTON + EMBED PDF */}
          <div className="mb-10">
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => setShowPdf((v) => !v)}
                className="px-4 py-2 text-xs sm:text-sm rounded-full bg-[#003B5C] text-white hover:bg-[#00263C] transition"
              >
                {showPdf ? "Hide MICE Brochure (PDF)" : "Show MICE Brochure (PDF)"}
              </button>
              {/* <a
                href={MICE_PDF_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs sm:text-sm text-[#00778B] underline"
              >
                Open in new tab
              </a> */}
            </div>

            {showPdf && (
              <div className="mt-4 w-full max-w-5xl mx-auto h-[420px] sm:h-[520px]">
                <iframe
                  src={getDrivePreviewUrl(MICE_PDF_URL)}
                  className="w-full h-full rounded-lg border border-zinc-200 dark:border-zinc-700"
                  loading="lazy"
                  allow="autoplay"
                />
              </div>
            )}
          </div>

          {err && <p className="text-red-500 text-sm mb-6">{err}</p>}

          <div className="relative">
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div
                    key={`sk-${i}`}
                    className="animate-pulse h-80 bg-zinc-200 dark:bg-zinc-800 rounded-2xl"
                  />
                ))}
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
                  {prodSlice.map((product) => (
                    <ProductCard
                      key={product.id}
                      product={product}
                      href={`/product/${product.id}`}
                      onAddToCart={handleAddToCart}
                    />
                  ))}
                </div>

                {allProducts.length === 0 && !loading && (
                  <p className="text-center text-zinc-500 py-10">
                    No products found in this category.
                  </p>
                )}
              </>
            )}

            {/* pagination */}
            <div className="mt-10 flex justify-center items-center gap-4 text-sm">
              <button
                disabled={prodPage <= 1}
                onClick={() => setProdPage((p) => p - 1)}
                className="px-3 py-1 bg-white dark:bg-zinc-900 border rounded disabled:opacity-50"
              >
                Prev
              </button>
              <span>
                {prodPage} / {prodTotalPages}
              </span>
              <button
                disabled={prodPage >= prodTotalPages}
                onClick={() => setProdPage((p) => p + 1)}
                className="px-3 py-1 bg-white dark:bg-zinc-900 border rounded disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        </div>

        {/* FLOATING CART */}
        {showCart && cartItems.length > 0 && (
          <div className="fixed bottom-4 right-4 z-40 bg-white dark:bg-zinc-900 shadow-lg border border-zinc-200 dark:border-zinc-700 rounded-xl p-3 w-72 max-h-80 flex flex-col">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-semibold">
                Cart ({cartCount} item{cartCount > 1 ? "s" : ""})
              </span>
              <button
                type="button"
                className="text-[10px] text-zinc-500 hover:text-zinc-800"
                onClick={() => setShowCart(false)}
              >
                Close
              </button>
            </div>
            <div className="flex-1 overflow-y-auto space-y-1 text-xs">
              {cartItems.map((item) => (
                <div
                  key={item.id}
                  className="flex justify-between gap-2 border-b border-dashed border-zinc-200 dark:border-zinc-700 pb-1"
                >
                  <div className="flex-1">
                    <div className="font-medium line-clamp-1">
                      {item.name}
                    </div>
                    <div className="text-[10px] text-zinc-500">
                      {item.quantity} × Rp{formatPriceDot(item.price)}
                    </div>
                    {item.store?.name && (
                      <div className="text-[10px] text-zinc-400">
                        {item.store.name}
                      </div>
                    )}
                  </div>
                  <div className="text-[10px] text-zinc-600">
                    Rp{formatPriceDot(item.quantity * item.price)}
                  </div>
                </div>
              ))}
            </div>
            <Link href="/cart">
              <button
                className={`mt-2 w-full text-xs font-semibold py-1.5 rounded-full ${tw.primaryBg} text-white`}
                type="button"
              >
                Review Order
              </button>
            </Link>
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
}
